
import { Link } from "react-router-dom";
import { Linkedin, MessageCircle, Phone, PhoneCall, Send, Youtube } from "lucide-react";
import paymentCards from "@/assets/payment-cards.png";
import idramLogo from "@/assets/idram-logo.png";
import arcaLogo from "@/assets/arca-logo.png";
import { useI18n } from "@/i18n/LanguageContext";

const linkClass = "text-sm text-muted-foreground transition-colors hover:text-highlight";
const iconClass = "inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/70 text-foreground transition-colors hover:border-primary hover:text-highlight";

export const Footer = () => {
  const { t } = useI18n();




  const siteLinks = [
    { to: "/", label: t.nav.home },
    { to: "/news", label: t.nav.news },
    { to: "/admission", label: t.nav.admission },
    { to: "/services", label: t.nav.services },
    { to: "/contact", label: t.nav.contact },
    { to: "/partnership", label: t.footer.partnership },
    { to: "/gift-cards", label: t.footer.giftCards },
  ];

  const courseLinks = [
    { to: "/ai-teacher-training", label: t.courses.items.aiTeacher.title },
    { to: "/cybersecurity", label: t.courses.items.cyber.title },
    { to: "/web-programming", label: t.courses.items.web.title },
    { to: "/ai", label: t.courses.items.ai.title },
    { to: "/kids-programming", label: t.courses.items.kids.title },
    { to: "/courses/qa-testing", label: t.courses.items.qa.title },
  ];

  const legalLinks = [
    { to: "/certificate-verification", label: t.footer.certificate },
    { to: "/social-refund-application", label: t.footer.socialRefund },
    { to: "/privacy-policy", label: t.footer.privacy },
    { to: "/refund-policy", label: t.footer.refund },
  ];

  return (
    <footer className="mt-auto w-full border-t border-border bg-background">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div className="min-w-0">
            <Link to="/" className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <img src="/new-logo.png" alt="Global IT Academy" className="brand-logo h-9 w-auto" width="151" height="36" loading="lazy" decoding="async" />
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-6 text-muted-foreground">{t.footer.tagline}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              <a href="tel:+37498960959" className="inline-flex h-10 items-center gap-2 rounded-md border border-border/70 px-3 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-highlight">
                <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
                +374 98 960959
              </a>
              <a href="https://t.me/globalitacademy23" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className={iconClass}>
                <Send className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href="https://wa.me/37498960959" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className={iconClass}>
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href="viber://chat?number=37498960959" aria-label="Viber" className={iconClass}>
                <PhoneCall className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href="https://www.youtube.com/@globalit.academy" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className={iconClass}>
                <Youtube className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href="https://am.linkedin.com/company/globalitcompany" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className={iconClass}>
                <Linkedin className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>

          <nav aria-label={t.footer.navTitle} className="min-w-0">
            <p className="mb-4 text-xs font-bold uppercase tracking-wide text-foreground">{t.footer.navTitle}</p>
            <ul className="space-y-2.5">
              {siteLinks.map((link) => (
                <li key={link.to}><Link to={link.to} className={linkClass}>{link.label}</Link></li>
              ))}
            </ul>
          </nav>

          <nav aria-label={t.footer.coursesTitle} className="min-w-0">
            <p className="mb-4 text-xs font-bold uppercase tracking-wide text-foreground">{t.footer.coursesTitle}</p>
            <ul className="space-y-2.5">
              {courseLinks.map((link) => (
                <li key={link.to}><Link to={link.to} className={linkClass}>{link.label}</Link></li>
              ))}
            </ul>
          </nav>

          <nav aria-label={t.footer.legalTitle} className="min-w-0">
            <p className="mb-4 text-xs font-bold uppercase tracking-wide text-foreground">{t.footer.legalTitle}</p>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.to}><Link to={link.to} className={linkClass}>{link.label}</Link></li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-border/60 pt-6 sm:flex-row">
          <p className="text-center text-xs text-muted-foreground sm:text-left">{t.footer.rights}</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <div className="rounded-md border border-border bg-white px-3 py-1.5">
              <img src={paymentCards} alt="Visa Mastercard" className="h-5 object-contain" width="73" height="20" loading="lazy" />
            </div>
            <div className="rounded-md border border-border bg-white px-3 py-1.5">
              <img src={idramLogo} alt="Idram" className="h-5 object-contain" width="69" height="20" loading="lazy" />
            </div>
            <div className="rounded-md border border-border bg-white px-3 py-1.5">
              <img src={arcaLogo} alt="Arca" className="h-5 object-contain" width="79" height="20" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
